import { type QueryKey, skipToken, useInfiniteQuery } from '@tanstack/react-query';
import { isObject } from 'lodash';
import { useMemo } from 'react';

import { Workspaces } from '@air/api';
import { ShortUrlObjectType, type WorkspaceInfo } from '@air/types';
import { isAirrorInstance } from '@air/utils-error';

export type UseListShortUrlsParams = {
  workspaceId: WorkspaceInfo['id'] | undefined;
  search?: string;
  limit?: number;
  orderBy?: {
    field: 'name' | 'createdAt' | 'expirationDate';
    direction: 'asc' | 'desc';
  };
};

export const SHORT_URLS_LIST = 'SHORT_URLS_LIST';
export type ShortUrlsListKey = [typeof SHORT_URLS_LIST, UseListShortUrlsParams];

export const getShortUrlsListKey = (params: UseListShortUrlsParams): ShortUrlsListKey => [
  SHORT_URLS_LIST,
  {
    workspaceId: params.workspaceId,
    search: params.search,
    limit: params.limit,
    orderBy: params.orderBy,
  },
];

export const isShortUrlsListKey = (key: QueryKey): key is ShortUrlsListKey => {
  return Array.isArray(key) && key[0] === SHORT_URLS_LIST && isObject(key[1]);
};

export const useListShortUrls = (params: UseListShortUrlsParams) => {
  const workspaceId = params.workspaceId;
  const query = useInfiniteQuery({
    queryKey: getShortUrlsListKey(params),
    queryFn: workspaceId
      ? async ({ pageParam }) => {
          const result = await Workspaces.ShortUrl.list({
            workspaceId,
            objectType: ShortUrlObjectType.collection,
            search: params.search,
            limit: params.limit,
            cursor: pageParam || undefined,
            orderBy: params.orderBy,
          });

          if (isAirrorInstance(result)) {
            throw result;
          }

          return result;
        }
      : skipToken,
    enabled: !!workspaceId,
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor || null,
    initialPageParam: '',
  });

  const flattenedData = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);

  const data = useMemo(
    () =>
      query.data
        ? {
            data: flattenedData,
            total: query.data.pages[0]?.total ?? 0,
            pagination: query.data.pages[query.data.pages.length - 1]?.pagination ?? {
              cursor: null,
              hasMore: false,
            },
          }
        : undefined,
    [query.data, flattenedData],
  );

  return {
    data,
    isLoading: query.isLoading,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
  };
};
